import React, { useState, useEffect, useRef } from 'react';
import { db } from './firebase';
import { collection, addDoc, query, where, orderBy, onSnapshot, getDocs, Timestamp, limit } from 'firebase/firestore';
import { Card, Button, Input, Alert } from 'antd';
import { BellOutlined, ClockCircleOutlined, UserOutlined, CheckCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';

export default function WaitList() {
  const [step, setStep] = useState('initial');
  const [formData, setFormData] = useState({ prenom: '', nom: '' });
  const [queuePosition, setQueuePosition] = useState(null);
  const [estimatedWaitTime, setEstimatedWaitTime] = useState(null);
  const [totalInQueue, setTotalInQueue] = useState(0);
  const [availableSpots, setAvailableSpots] = useState(0);
  const [userQueueId, setUserQueueId] = useState(null);
  const [error, setError] = useState('');
  const [nextAvailableTime, setNextAvailableTime] = useState(null);
  const audioRef = useRef(null);

  const LIBRARY_CAPACITY = 57;

  useEffect(() => {
    const audioContext = new (window.AudioContext || window.webkitAudioContext)();
    const beep = () => {
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = 800;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.5);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.5);
    };
    
    audioRef.current = beep;
  }, []);

  useEffect(() => {
    const visiteurQuery = query(
      collection(db, 'visiteur'),
      where('sortie', '==', false)
    );

    const unsubscribe = onSnapshot(visiteurQuery, (snapshot) => {
      const activeVisitors = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      const spots = LIBRARY_CAPACITY - activeVisitors.length;
      setAvailableSpots(Math.max(0, spots));

      if (activeVisitors.length > 0) {
        const exitTimes = activeVisitors
          .map(v => {
            if (v.startTime && v.hours) {
              const start = v.startTime.toDate();
              const exitTime = new Date(start.getTime() + (v.hours * 60 * 60 * 1000));
              return exitTime;
            }
            return null;
          })
          .filter(t => t !== null)
          .sort((a, b) => a - b);

        if (exitTimes.length > 0) {
          setNextAvailableTime(exitTimes[0]);
        }
      } else {
        setNextAvailableTime(null);
      }

      if (userQueueId && queuePosition && queuePosition <= spots) {
        playNotification();
        setStep('available');
      }
    });

    return () => unsubscribe();
  }, [userQueueId, queuePosition]);

  useEffect(() => {
    const queueQuery = query(
      collection(db, 'liste_attente'),
      where('statut', '==', 'en_attente'),
      orderBy('rang', 'asc')
    );

    const unsubscribe = onSnapshot(queueQuery, (snapshot) => {
      setTotalInQueue(snapshot.size);

      if (userQueueId) {
        const userDoc = snapshot.docs.find(doc => doc.id === userQueueId);
        if (userDoc) {
          const userData = userDoc.data();
          setQueuePosition(userData.rang);
          calculateWaitTime(userData.rang);
        }
      }
    });

    return () => unsubscribe();
  }, [userQueueId]);

  const calculateWaitTime = async (position) => {
    try {
      const visiteurQuery = query(
        collection(db, 'visiteur'),
        where('sortie', '==', false)
      );
      
      const snapshot = await getDocs(visiteurQuery);
      const visitors = snapshot.docs.map(doc => doc.data());

      if (visitors.length === 0) {
        setEstimatedWaitTime(0);
        return;
      }

      const exitTimes = visitors
        .map(v => {
          if (v.startTime && v.hours) {
            const start = v.startTime.toDate();
            return new Date(start.getTime() + (v.hours * 60 * 60 * 1000));
          }
          return null;
        })
        .filter(t => t !== null)
        .sort((a, b) => a - b);

      const relevantExitTimeIndex = Math.min(position - 1, exitTimes.length - 1);
      if (exitTimes[relevantExitTimeIndex]) {
        const waitUntil = exitTimes[relevantExitTimeIndex];
        const now = new Date();
        const waitMinutes = Math.max(0, Math.round((waitUntil - now) / 60000));
        setEstimatedWaitTime(waitMinutes);
      }
    } catch (err) {
      console.error('Error calculating wait time:', err);
    }
  };

  const playNotification = () => {
    if (audioRef.current) {
      try {
        audioRef.current();
        setTimeout(() => audioRef.current(), 300);
        setTimeout(() => audioRef.current(), 600);
      } catch (err) {
        console.log('Audio play failed:', err);
      }
    }

    if ('vibrate' in navigator) {
      navigator.vibrate([200, 100, 200, 100, 200]);
    }

    if ('Notification' in window && Notification.permission === 'granted') {
      new Notification('WeStudy - Place disponible!', {
        body: 'Une place est maintenant disponible. Présentez-vous à l\'accueil.',
        icon: '/logo.png',
        badge: '/logo.png'
      });
    }
  };

  const requestNotificationPermission = async () => {
    if ('Notification' in window && Notification.permission === 'default') {
      await Notification.requestPermission();
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (!formData.prenom.trim() || !formData.nom.trim()) {
      setError('Veuillez remplir tous les champs');
      return;
    }

    try {
      await requestNotificationPermission();

      const queueQuery = query(
        collection(db, 'liste_attente'),
        orderBy('rang', 'desc'),
        limit(1)
      );
      
      const snapshot = await getDocs(queueQuery);
      const maxRang = snapshot.empty ? 0 : snapshot.docs[0].data().rang;
      const newRang = maxRang + 1;

      const docRef = await addDoc(collection(db, 'liste_attente'), {
        prenom: formData.prenom.trim(),
        nom: formData.nom.trim(),
        fullname: `${formData.prenom.trim()} ${formData.nom.trim()}`,
        rang: newRang,
        heure_inscription: Timestamp.now(),
        statut: 'en_attente',
        notifie: false
      });

      setUserQueueId(docRef.id);
      setQueuePosition(newRang);
      setStep('waiting');
      
      await calculateWaitTime(newRang);
    } catch (err) {
      console.error('Error joining queue:', err);
      setError('Erreur lors de l\'inscription. Veuillez réessayer.');
    }
  };

  const formatWaitTime = (minutes) => {
    if (minutes === null || minutes === undefined) return 'Calcul en cours...';
    if (minutes === 0) return 'Bientôt disponible !';
    if (minutes < 60) return `≈ ${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return `≈ ${hours}h${mins > 0 ? ` ${mins}min` : ''}`;
  };

  const formatTime = (date) => {
    if (!date) return '';
    return date.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div
      style={{
        background: 'linear-gradient(135deg, #1f2937 0%, #111827 100%)',
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem',
        fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
      }}
    >
      <Card
        style={{
          width: '100%',
          maxWidth: '28rem',
          background: '#1f2937',
          borderColor: '#374151',
          borderRadius: '1rem',
          boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
        }}
      >
        {/* Header */}
        <div
          style={{
            textAlign: 'center',
            borderBottom: '1px solid #374151',
            paddingBottom: '1.5rem',
            marginBottom: '1.5rem',
          }}
        >
          <div
            style={{
              width: '4rem',
              height: '4rem',
              background: '#2563eb',
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1rem',
            }}
          >
            <ClockCircleOutlined style={{ fontSize: '2rem', color: '#fff' }} />
          </div>
          <h1 style={{ fontSize: '1.5rem', fontWeight: '700', color: '#fff', margin: 0 }}>
            Liste d'Attente
          </h1>
          <p style={{ color: '#9ca3af', marginTop: '0.5rem', fontSize: '0.875rem' }}>
            WeStudy - Gestion des places
          </p>
        </div>

        {/* Availability Status */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '1rem',
            background: 'linear-gradient(90deg, #374151 0%, #4b5563 100%)',
            borderRadius: '0.5rem',
            border: '1px solid #4b5563',
            marginBottom: '1.5rem',
          }}
        >
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.25rem' }}>
              <UserOutlined style={{ fontSize: '1.5rem', color: '#60a5fa' }} />
              <span style={{ color: '#d1d5db', fontSize: '0.875rem' }}>Places disponibles</span>
            </div>
            {nextAvailableTime && availableSpots === 0 && (
              <div style={{ color: '#9ca3af', fontSize: '0.75rem', marginLeft: '2.25rem' }}>
                Prochaine: {formatTime(nextAvailableTime)}
              </div>
            )}
          </div>
          <span
            style={{
              fontSize: '2rem',
              fontWeight: '700',
              color: availableSpots > 0 ? '#34d399' : '#f87171',
            }}
          >
            {availableSpots}
          </span>
        </div>

        {/* Initial Step */}
        {step === 'initial' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <Alert
              type={availableSpots > 0 ? 'success' : 'info'}
              icon={<ExclamationCircleOutlined />}
              message={
                availableSpots > 0
                  ? `✓ Il y a ${availableSpots} place(s) disponible(s) actuellement.`
                  : `La bibliothèque est complète. ${totalInQueue > 0 ? `${totalInQueue} personne(s) en attente.` : ''}`
              }
              style={{
                background: availableSpots > 0 ? 'rgba(16, 185, 129, 0.1)' : 'rgba(59, 130, 246, 0.1)',
                border: `1px solid ${availableSpots > 0 ? '#059669' : '#2563eb'}`,
                color: '#d1d5db',
              }}
            />

            {totalInQueue > 0 && (
              <div
                style={{
                  textAlign: 'center',
                  padding: '0.75rem',
                  background: '#374151',
                  borderRadius: '0.5rem',
                }}
              >
                <span style={{ fontSize: '1.5rem', fontWeight: '700', color: '#fb923c' }}>
                  {totalInQueue}
                </span>
                <span style={{ color: '#9ca3af', fontSize: '0.875rem', marginLeft: '0.5rem' }}>
                  personne(s) en attente
                </span>
              </div>
            )}

            <Button
              type="primary"
              size="large"
              onClick={() => setStep('form')}
              style={{
                width: '100%',
                height: '3rem',
                fontSize: '1.125rem',
                fontWeight: '600',
                background: '#2563eb',
                borderColor: '#2563eb',
              }}
            >
              S'inscrire dans la queue
            </Button>
          </div>
        )}

        {/* Form Step */}
        {step === 'form' && (
          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: '1.25rem' }}>
            <div>
              <label style={{ display: 'block', color: '#d1d5db', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                Prénom *
              </label>
              <Input
                value={formData.prenom}
                onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                placeholder="Votre prénom"
                size="large"
                required
                autoFocus
                style={{
                  background: '#374151',
                  borderColor: '#4b5563',
                  color: '#fff',
                }}
              />
            </div>

            <div>
              <label style={{ display: 'block', color: '#d1d5db', marginBottom: '0.5rem', fontSize: '0.875rem' }}>
                Nom *
              </label>
              <Input
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                placeholder="Votre nom"
                size="large"
                required
                style={{
                  background: '#374151',
                  borderColor: '#4b5563',
                  color: '#fff',
                }}
              />
            </div>

            {error && (
              <Alert
                type="error"
                icon={<ExclamationCircleOutlined />}
                message={error}
                style={{
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid #dc2626',
                  color: '#fca5a5',
                }}
              />
            )}

            <div style={{ display: 'flex', gap: '0.75rem', paddingTop: '0.5rem' }}>
              <Button
                onClick={() => setStep('initial')}
                size="large"
                style={{
                  flex: 1,
                  height: '3rem',
                  background: '#374151',
                  borderColor: '#4b5563',
                  color: '#fff',
                }}
              >
                Retour
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                size="large"
                style={{
                  flex: 1,
                  height: '3rem',
                  fontWeight: '600',
                  background: '#2563eb',
                  borderColor: '#2563eb',
                }}
              >
                Confirmer
              </Button>
            </div>
          </form>
        )}

        {/* Waiting Step */}
        {step === 'waiting' && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div style={{ textAlign: 'center' }}>
              <div
                style={{
                  width: '5rem',
                  height: '5rem',
                  background: '#2563eb',
                  borderRadius: '50%',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginBottom: '1rem',
                  animation: 'pulse 2s infinite',
                }}
              >
                <ClockCircleOutlined style={{ fontSize: '2.5rem', color: '#fff' }} />
              </div>
              <h2 style={{ fontSize: '1.5rem', fontWeight: '600', color: '#fff', margin: 0 }}>
                Vous êtes en attente
              </h2>
              <p style={{ color: '#9ca3af', fontSize: '0.875rem', marginTop: '0.5rem' }}>
                {formData.prenom} {formData.nom}
              </p>
              <p style={{ color: '#6b7280', fontSize: '0.875rem', marginTop: '0.25rem' }}>
                Restez à proximité, nous vous notifierons
              </p>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div
                style={{
                  padding: '1.25rem',
                  background: 'linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%)',
                  borderRadius: '0.5rem',
                  textAlign: 'center',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                }}
              >
                <div style={{ fontSize: '2.5rem', fontWeight: '700', color: '#fff', marginBottom: '0.25rem' }}>
                  {queuePosition}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#bfdbfe' }}>Position</div>
              </div>
              <div
                style={{
                  padding: '1.25rem',
                  background: 'linear-gradient(135deg, #059669 0%, #047857 100%)',
                  borderRadius: '0.5rem',
                  textAlign: 'center',
                  boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                }}
              >
                <div style={{ fontSize: '1.5rem', fontWeight: '700', color: '#fff', marginBottom: '0.25rem' }}>
                  {formatWaitTime(estimatedWaitTime)}
                </div>
                <div style={{ fontSize: '0.875rem', color: '#a7f3d0' }}>Attente estimée</div>
              </div>
            </div>

            <Alert
              type="warning"
              icon={<BellOutlined />}
              message={
                <span>
                  <strong>Notification active:</strong> Un signal sonore retentira lorsqu'une place se libère avant l'heure prévue.
                </span>
              }
              style={{
                background: 'rgba(55, 65, 81, 0.5)',
                border: '1px solid #4b5563',
                color: '#d1d5db',
              }}
            />

            {availableSpots > 0 && queuePosition > availableSpots && (
              <Alert
                type="info"
                icon={<ExclamationCircleOutlined />}
                message={`Il y a ${availableSpots} place(s) disponible(s), mais ${queuePosition - availableSpots} personne(s) sont devant vous.`}
                style={{
                  background: 'rgba(251, 146, 60, 0.1)',
                  border: '1px solid #ea580c',
                  color: '#fdba74',
                }}
              />
            )}
          </div>
        )}

        {/* Available Step */}
        {step === 'available' && (
          <div style={{ textAlign: 'center', display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            <div
              style={{
                width: '6rem',
                height: '6rem',
                background: '#059669',
                borderRadius: '50%',
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto',
                animation: 'bounce 1s infinite',
                boxShadow: '0 0 30px rgba(5, 150, 105, 0.5)',
              }}
            >
              <CheckCircleOutlined style={{ fontSize: '3rem', color: '#fff' }} />
            </div>
            <div>
              <h2 style={{ fontSize: '2rem', fontWeight: '700', color: '#34d399', margin: 0 }}>
                C'est votre tour !
              </h2>
              <p style={{ fontSize: '1.25rem', color: '#d1d5db', marginTop: '0.5rem' }}>
                Une place est disponible
              </p>
            </div>
            <div
              style={{
                background: 'rgba(5, 150, 105, 0.1)',
                border: '1px solid #059669',
                borderRadius: '0.5rem',
                padding: '1rem',
              }}
            >
              <p style={{ color: '#d1d5db', margin: 0 }}>
                Veuillez vous présenter à <strong style={{ color: '#34d399' }}>l'accueil immédiatement</strong>
              </p>
            </div>
            <Button
              type="primary"
              size="large"
              onClick={() => {
                setStep('initial');
                setUserQueueId(null);
                setQueuePosition(null);
                setFormData({ prenom: '', nom: '' });
              }}
              style={{
                width: '100%',
                height: '3.5rem',
                fontSize: '1.125rem',
                fontWeight: '600',
                background: '#059669',
                borderColor: '#059669',
              }}
            >
              J'ai compris
            </Button>
          </div>
        )}
      </Card>

      {/* Animations CSS */}
      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.5; }
        }
        @keyframes bounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-10px); }
        }
      `}</style>
    </div>
  );
}